import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  Check, 
  ArrowRight, 
  AlertTriangle,
  QrCode,
  Link as LinkIcon,
  FileText,
  Building2,
  Users,
  Store,
  ShoppingBag,
  Lock,
  Eye,
  Send,
  Clock,
  HelpCircle,
  ChevronRight
} from 'lucide-react';

export default function VeriFactuPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isSpanish = i18n.language === 'es';

  const faqs = [
    {
      question: isSpanish 
        ? '¿Cuándo entra en vigor VeriFactu?' 
        : 'When does VeriFactu come into effect?',
      answer: isSpanish
        ? 'VeriFactu entrará en vigor progresivamente. Los sistemas de facturación deben estar adaptados para cumplir con los requisitos técnicos establecidos por la AEAT.'
        : 'VeriFactu will come into effect progressively. Invoicing systems must be adapted to meet the technical requirements set by AEAT.'
    },
    {
      question: isSpanish 
        ? '¿Qué pasa si no cumplo con VeriFactu?' 
        : 'What happens if I don\'t comply with VeriFactu?',
      answer: isSpanish
        ? 'El incumplimiento puede resultar en sanciones significativas. La normativa antifraude establece multas para empresas que utilicen software no certificado o que manipulen registros de facturación.'
        : 'Non-compliance can result in significant penalties. The anti-fraud regulation establishes fines for companies using non-certified software or manipulating billing records.'
    },
    {
      question: isSpanish 
        ? '¿Hambre está certificado para VeriFactu?' 
        : 'Is Hambre certified for VeriFactu?',
      answer: isSpanish
        ? 'Sí, Hambre cumple con todos los requisitos técnicos de VeriFactu: cadena de hash criptográfica, códigos QR verificables, envío automático a la AEAT, y soporte completo del IVA español.'
        : 'Yes, Hambre meets all VeriFactu technical requirements: cryptographic hash chain, verifiable QR codes, automatic AEAT submission, and full Spanish VAT support.'
    },
    {
      question: isSpanish 
        ? '¿Necesito hacer algo para activar VeriFactu?' 
        : 'Do I need to do anything to activate VeriFactu?',
      answer: isSpanish
        ? 'Solo necesitas introducir los datos fiscales de tu negocio (NIF, razón social, dirección fiscal) en la configuración de Hambre. El sistema generará automáticamente facturas conformes a VeriFactu.'
        : 'You only need to enter your business fiscal data (NIF, company name, fiscal address) in Hambre settings. The system will automatically generate VeriFactu-compliant invoices.'
    },
    {
      question: isSpanish 
        ? '¿VeriFactu funciona con mi impresora de tickets actual?' 
        : 'Does VeriFactu work with my current receipt printer?',
      answer: isSpanish
        ? 'Sí, Hambre imprime tickets con código QR y toda la información requerida por VeriFactu en cualquier impresora térmica estándar (ESC/POS).'
        : 'Yes, Hambre prints receipts with QR codes and all VeriFactu required information on any standard thermal printer (ESC/POS).'
    }
  ];

  return (
    <>
      <SEO page="verifactu" />
      
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20 pb-24">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }} />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-full px-4 py-2 mb-6">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-green-300 font-medium">
                  {isSpanish ? 'Cumplimiento Fiscal España' : 'Spain Fiscal Compliance'}
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                {isSpanish ? '¿Qué es' : 'What is'} <span className="text-green-400">VeriFactu</span>?
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
                {isSpanish 
                  ? 'VeriFactu es el nuevo sistema antifraude de facturación de España. Garantiza que las facturas no puedan ser manipuladas o eliminadas.'
                  : 'VeriFactu is Spain\'s new anti-fraud invoicing system. It ensures invoices cannot be manipulated or deleted.'}
              </p>

              {/* Alert Box */}
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-6 max-w-2xl mx-auto mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-red-300 mb-1">
                      {isSpanish ? 'VeriFactu es OBLIGATORIO' : 'VeriFactu is MANDATORY'}
                    </h3>
                    <p className="text-red-200">
                      {isSpanish 
                        ? 'La normativa antifraude de España exige que todos los negocios con software de facturación cumplan con VeriFactu. Esto incluye restaurantes, bares, tiendas y autónomos.'
                        : 'Spain\'s anti-fraud regulation requires ALL businesses using invoicing software to comply with VeriFactu. This includes restaurants, bars, retail shops, and freelancers.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/register?role=restaurant_admin')}
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  {isSpanish ? 'Empezar con Hambre' : 'Get Started with Hambre'} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    const el = document.getElementById('who-needs');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="border-slate-500 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
                >
                  {isSpanish ? 'Más Información' : 'Learn More'}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Who Needs VeriFactu */}
        <section className="py-20 bg-slate-50" id="who-needs">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {isSpanish ? '¿Quién necesita cumplir con VeriFactu?' : 'Who Needs to Comply with VeriFactu?'}
              </h2>
              <p className="text-lg text-slate-600">
                {isSpanish 
                  ? 'VeriFactu aplica a TODOS los negocios en España que emiten facturas usando software'
                  : 'VeriFactu applies to ALL businesses in Spain that issue invoices using software'}
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 text-center">
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
                  <Store className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Restaurantes y Bares' : 'Restaurants & Bars'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish ? 'Todos los establecimientos de hostelería' : 'All hospitality establishments'}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Empresas (SL, SA)' : 'Companies (SL, SA)'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish ? 'Sociedades limitadas y anónimas' : 'Limited and public companies'}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Autónomos' : 'Freelancers'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish ? 'Trabajadores por cuenta propia' : 'Self-employed workers'}
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Comercio Minorista' : 'Retail'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isSpanish ? 'Tiendas y comercios' : 'Shops and stores'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What VeriFactu Requires */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {isSpanish ? '¿Qué exige VeriFactu?' : 'What Does VeriFactu Require?'}
              </h2>
              <p className="text-lg text-slate-600">
                {isSpanish 
                  ? 'Tu software de facturación debe garantizar estos requisitos técnicos'
                  : 'Your invoicing software must guarantee these technical requirements'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/30">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Integridad' : 'Integrity'}
                </h3>
                <p className="text-slate-600">
                  {isSpanish 
                    ? 'Las facturas no pueden ser modificadas ni eliminadas después de su emisión'
                    : 'Invoices cannot be modified or deleted after issuance'}
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
                  <LinkIcon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Trazabilidad' : 'Traceability'}
                </h3>
                <p className="text-slate-600">
                  {isSpanish 
                    ? 'Cadena criptográfica que conecta todas las facturas - imposible de manipular'
                    : 'Cryptographic chain connecting all invoices - impossible to tamper'}
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/30">
                  <QrCode className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Código QR' : 'QR Code'}
                </h3>
                <p className="text-slate-600">
                  {isSpanish 
                    ? 'Cada factura incluye un código QR verificable por la AEAT'
                    : 'Every invoice includes a QR code verifiable by AEAT'}
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
                  <Send className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {isSpanish ? 'Envío a AEAT' : 'AEAT Submission'}
                </h3>
                <p className="text-slate-600">
                  {isSpanish 
                    ? 'Envío automático de registros a la Agencia Tributaria'
                    : 'Automatic submission of records to Tax Authority'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Hambre is Ready */}
        <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-full px-4 py-2 mb-4">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">
                    {isSpanish ? '100% Preparado' : '100% Ready'}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  {isSpanish ? 'Hambre cumple con VeriFactu' : 'Hambre is VeriFactu Compliant'}
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  {isSpanish 
                    ? 'No te preocupes por la normativa. Hambre gestiona automáticamente todos los requisitos de VeriFactu para que puedas centrarte en tu negocio.'
                    : 'Don\'t worry about regulations. Hambre automatically handles all VeriFactu requirements so you can focus on your business.'}
                </p>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900">
                        {isSpanish ? 'Cadena Hash SHA-256' : 'SHA-256 Hash Chain'}
                      </span>
                      <p className="text-slate-600">
                        {isSpanish 
                          ? 'Cada factura está criptográficamente vinculada a la anterior'
                          : 'Every invoice is cryptographically linked to the previous one'}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900">
                        {isSpanish ? 'Códigos QR Automáticos' : 'Automatic QR Codes'}
                      </span>
                      <p className="text-slate-600">
                        {isSpanish 
                          ? 'Todos los tickets incluyen código QR verificable'
                          : 'All receipts include verifiable QR code'}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900">
                        {isSpanish ? 'Integración AEAT' : 'AEAT Integration'}
                      </span>
                      <p className="text-slate-600">
                        {isSpanish 
                          ? 'Envío automático a la Agencia Tributaria'
                          : 'Automatic submission to Spanish Tax Authority'}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900">
                        {isSpanish ? 'IVA Español Completo' : 'Full Spanish VAT'}
                      </span>
                      <p className="text-slate-600">
                        {isSpanish 
                          ? 'Soporte para todos los tipos: 21%, 10%, 4%, 0%'
                          : 'Support for all rates: 21%, 10%, 4%, 0%'}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="font-bold text-slate-900">
                        {isSpanish ? 'Sin Bloqueos' : 'No Blocking'}
                      </span>
                      <p className="text-slate-600">
                        {isSpanish 
                          ? 'Tu TPV sigue funcionando aunque haya problemas de conexión'
                          : 'Your POS keeps working even with connection issues'}
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-xl border border-green-200">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Shield className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {isSpanish ? 'Certificado VeriFactu' : 'VeriFactu Certified'}
                  </h3>
                  <p className="text-green-600 font-medium">Hambre Restaurant OS</p>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <ul className="space-y-3 text-sm">
                    <li className="flex justify-between">
                      <span className="text-slate-600">{isSpanish ? 'Algoritmo Hash' : 'Hash Algorithm'}</span>
                      <span className="font-medium text-slate-900">SHA-256</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-slate-600">{isSpanish ? 'Cadena de Facturas' : 'Invoice Chain'}</span>
                      <span className="font-medium text-green-600">{isSpanish ? 'Activo' : 'Active'}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-slate-600">{isSpanish ? 'Códigos QR' : 'QR Codes'}</span>
                      <span className="font-medium text-green-600">{isSpanish ? 'Incluidos' : 'Included'}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-slate-600">{isSpanish ? 'Envío AEAT' : 'AEAT Submission'}</span>
                      <span className="font-medium text-green-600">{isSpanish ? 'Automático' : 'Automatic'}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-slate-600">{isSpanish ? 'Tipos IVA' : 'VAT Types'}</span>
                      <span className="font-medium text-slate-900">21%, 10%, 4%, 0%</span>
                    </li>
                  </ul>
                </div>

                <Button 
                  onClick={() => navigate('/register?role=restaurant_admin')}
                  className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white rounded-full py-4 font-semibold"
                >
                  {isSpanish ? 'Empezar Ahora' : 'Get Started Now'} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {isSpanish ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
              </h2>
              <p className="text-lg text-slate-600">
                {isSpanish 
                  ? 'Todo lo que necesitas saber sobre VeriFactu y Hambre'
                  : 'Everything you need to know about VeriFactu and Hambre'}
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-start gap-2">
                    <HelpCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-slate-600 ml-8">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {isSpanish 
                ? '¿Listo para cumplir con VeriFactu?' 
                : 'Ready to Comply with VeriFactu?'}
            </h2>
            <p className="text-xl text-green-100 mb-8">
              {isSpanish 
                ? 'Empieza a usar Hambre hoy y olvídate de preocupaciones fiscales'
                : 'Start using Hambre today and forget about fiscal worries'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/register?role=restaurant_admin')}
                className="bg-white text-green-600 hover:bg-green-50 rounded-full px-8 py-6 text-lg font-semibold shadow-lg"
              >
                {isSpanish ? 'Empezar Prueba Gratis' : 'Start Free Trial'} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
              >
                {isSpanish ? 'Contactar Ventas' : 'Contact Sales'}
              </Button>
            </div>
            <p className="text-green-200 mt-6 text-sm">
              {isSpanish 
                ? '14 días gratis. Sin tarjeta de crédito.'
                : '14-day free trial. No credit card required.'}
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
